import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { ChartBar, ArrowLeft, FolderSimple } from '@phosphor-icons/react';
import EconomicsPanel from '../components/economics/EconomicsPanel';
import api from '../services/api';

interface ProjectSummary {
  id: string;
  name: string;
  icao_code: string;
  iata_code: string;
  analysis_status: string;
}

const EconomicsPage: React.FC = () => {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const t = (es: string, en: string) => language === 'es' ? es : en;

  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [selectedProject, setSelectedProject] = useState<ProjectSummary | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get('/projects/');
      setProjects(res.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg)' }}>
      <div className="max-w-6xl mx-auto px-6 py-8">

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button onClick={() => navigate('/')} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            width: 36, height: 36, borderRadius: 10, border: '1px solid var(--border)',
            background: 'var(--surface)', cursor: 'pointer', color: 'var(--text-secondary)',
          }}>
            <ArrowLeft size={18} />
          </button>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ChartBar size={24} weight="bold" style={{ color: 'var(--aipron-accent)' }} />
              <h1 style={{
                fontSize: 28, fontWeight: 700, color: 'var(--text-primary)',
                fontFamily: 'var(--font-display)', letterSpacing: '-0.02em',
              }}>
                Economics
              </h1>
            </div>
            <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginTop: 2 }}>
              {t('CBA & ROI Engine — Análisis económico de proyectos', 'CBA & ROI Engine — Project economic analysis')}
            </p>
          </div>
        </div>

        {/* Project selector or panel */}
        {!selectedProject ? (
          <div>
            <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 16 }}>
              {t('Selecciona un proyecto', 'Select a project')}
            </h2>
            {loading ? (
              <p style={{ color: 'var(--text-tertiary)' }}>{t('Cargando...', 'Loading...')}</p>
            ) : projects.length === 0 ? (
              <p style={{ color: 'var(--text-tertiary)' }}>
                {t('No hay proyectos. Crea uno en DACAMS Analysis.', 'No projects. Create one in DACAMS Analysis.')}
              </p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 12 }}>
                {projects.map(p => (
                  <button key={p.id} onClick={() => setSelectedProject(p)} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: 16, borderRadius: 12,
                    background: 'var(--surface)', border: '1px solid var(--border)', cursor: 'pointer',
                    textAlign: 'left', transition: 'all 150ms',
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--aipron-accent)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                  >
                    <FolderSimple size={28} weight="duotone" style={{ color: 'var(--aipron-accent)', flexShrink: 0 }} />
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{p.name}</div>
                      <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>
                        {p.icao_code || p.iata_code || '—'} · {p.analysis_status}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div>
            <button onClick={() => setSelectedProject(null)} style={{
              display: 'flex', alignItems: 'center', gap: 6, marginBottom: 16, padding: '6px 12px',
              borderRadius: 8, background: 'var(--surface)', border: '1px solid var(--border)',
              cursor: 'pointer', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600,
            }}>
              <ArrowLeft size={14} /> {t('Cambiar proyecto', 'Change project')}
            </button>
            <EconomicsPanel projectId={selectedProject.id} projectName={selectedProject.name} />
          </div>
        )}
      </div>
    </div>
  );
};

export default EconomicsPage;
